/**
 * @file Gear inside the STRV IT Hub (https://ithub.strv.io).
 *
 * Outside the hub's frame this does nothing and Gear is the standalone app
 * at gear.strv.io. Inside it, the standard bridge (standard/js/bridge.js)
 * marks <html data-embed="hub"> — CSS then hides the logo, the profile block,
 * the theme switch and the phone bottom bar — and:
 *   • the hub decides the theme (never saved here)
 *   • the hub can send Gear to any page (hash routes are Gear's deep links)
 *   • every route change is reported back so the hub's address stays in sync
 */

import { connectToHub } from './standard/js/bridge.js';
import { applyTheme } from './standard/js/theme.js';
import { navigate, onRoute } from './router.js';

export const GEAR_VERSION = '2.0.0';

// Inside the hub, a signed-out Gear waits this long for the hub's sign-in
// proof before showing its own login screen (the hand-off takes ~2 s;
// people with no access to Gear in the hub never get one).
const SESSION_WAIT_MS = 6000;

let hub = null;
let waitingForSession = false;

/**
 * Connect once at boot. Session + sign-out handlers come from auth.js;
 * `onWaitEnd` runs when the wait for the hub's sign-in is over (signed in
 * or timed out), so the shell can stop showing its loading state.
 */
export function startHubBridge({ onSession, onSignOut, onWaitEnd } = {}) {
  let waitTimer = null;
  const endWait = () => {
    if (!waitingForSession) return;
    waitingForSession = false;
    clearTimeout(waitTimer);
    onWaitEnd?.();
  };
  hub = connectToHub({
    tool: 'gear',
    version: GEAR_VERSION,
    onTheme: (theme) => applyTheme(theme, { save: false }),
    onNavigate: (path) => navigate(path || '/'),
    onSession: (token) => Promise.resolve(onSession?.(token)).finally(endWait),
    onSignOut,
    // Local development of the hub frames a local Gear.
    extraOrigins: import.meta.env.DEV ? ['http://localhost:5173'] : [],
  });
  if (!hub) return null;
  waitingForSession = true;
  waitTimer = setTimeout(endWait, SESSION_WAIT_MS);
  onRoute((route) => hub.location(route.raw.replace(/^#/, '') || '/'));
  return hub;
}

/** True while a framed, signed-out Gear is still expecting the hub's sign-in. */
export function waitingForHubSession() {
  return waitingForSession;
}

/** True once the hub has said hello (Gear is shown inside it). */
export function isEmbedded() {
  return document.documentElement.dataset.embed === 'hub';
}

/** Hand the person to another hub tool; a plain new tab when standalone. */
export function openInTool(tool, path, fallbackUrl) {
  if (hub?.connected()) hub.open(tool, path);
  else window.open(fallbackUrl, '_blank', 'noopener');
}
